import { useState, useMemo, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { useAppStore } from '@/store'
import type { SymbolNode, TagNode } from '@/service/graph'
import { useTranslation } from '@/i18n'
import { Button } from '@/components/ui/button'
import { useTaxonomy } from './TaxonomyContext'
import {
  resolveClassification,
} from '@/config/pnidClassification'

interface LabelingFilterPanelV2Props {
  isOpen: boolean
  onClose: () => void
  /** Reports the matching objects to spotlight + frame on the drawing (FS-623). */
  onHighlightChange?: (ids: Set<string>) => void
  /** FS-623 focus mode: reports whether non-matched shapes should be dimmed so the
   *  spotlighted matches stand alone. */
  onFocusModeChange?: (on: boolean) => void
}

const EMPTY_SET: Set<string> = new Set()

// FS-623: sentinel Category value for "not classified yet" — matches nodes whose
// pnid_level_i is empty, so annotators can spotlight the objects still needing a
// label (a taxonomy filter otherwise can't surface un-labeled objects).
const UNCLASSIFIED = '__unclassified__'

/**
 * FS-623 — labeling filter. Lets an annotator find objects by any combination of
 * Category / Subcategory / Type / Tag / Annotation / Note and spotlights every
 * match on the drawing (neon halo, via `onHighlightChange` → `reviewHighlightIds`).
 * Complements the review-state-based Pending Review panel: this filters on the
 * annotation fields themselves, which is what actually speeds up labeling.
 */
export default function LabelingFilterPanelV2({ isOpen, onClose, onHighlightChange, onFocusModeChange }: LabelingFilterPanelV2Props) {
  const { t } = useTranslation()
  const taxonomy = useTaxonomy()
  const { currentState } = useAppStore()

  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [type, setType] = useState('')
  const [tag, setTag] = useState('')
  const [annotation, setAnnotation] = useState('')
  const [note, setNote] = useState('')
  // Focus matches (dim everything else) is ON by default (Armin): the pink spotlight is
  // the point of the filter, so start focused rather than lost among the other renderings.
  const [focusMode, setFocusMode] = useState(true)

  // Cascading dropdown options, driven by the taxonomy (pnidClassification.ts).
  const subcategoryOptions = useMemo(() => (category ? taxonomy.getLevelII(category) : []), [category, taxonomy])
  const typeOptions = useMemo(
    () => (category && subcategory ? taxonomy.getLevelIII(category, subcategory) : []),
    [category, subcategory, taxonomy],
  )

  const hasCriteria = !!(category || subcategory || type || tag.trim() || annotation.trim() || note.trim())

  // Every node that satisfies ALL non-empty criteria (AND-combined). Category /
  // Subcategory / Type are exact matches on the classification; Tag / Annotation /
  // Note are case-insensitive substring matches on the annotation text.
  const matches = useMemo(() => {
    // Guard computed inline (not via the derived `hasCriteria`) so the memo depends
    // only on the raw filter fields — no redundant/missing dependency either way.
    if (!(category || subcategory || type || tag.trim() || annotation.trim() || note.trim()))
      return [] as { id: string; label: string }[]
    const tagQ = tag.trim().toLowerCase()
    const annQ = annotation.trim().toLowerCase()
    const noteQ = note.trim().toLowerCase()
    const out: { id: string; label: string }[] = []
    for (const node of currentState.nodes.values()) {
      const savedLevelI = (node.pnid_level_i ?? '').trim()
      // Effective classification MUST mirror what NodeDetailsPanelV2 DISPLAYS: prefer the
      // saved pnid_level_i/ii/iii, else derive from the node type (+ legacy category) via
      // resolveClassification. A detected tag (type 'tag', empty stored fields) is
      // Annotation▸Text▸Tag by derivation — filtering only on the stored fields matched
      // nothing, so instrument/equipment tags (all tag nodes) were invisible to the
      // classification filter even though the details panel showed them as Annotation▸Text▸Tag.
      let effLevelI = savedLevelI
      let effLevelII = (node.pnid_level_ii ?? '').trim()
      let effLevelIII = (node.pnid_level_iii ?? '').trim()
      // Only symbol/tag/connection have a derivable taxonomy classification. Guard the
      // node type explicitly (rather than casting) so a `table_cell` node isn't fed to
      // resolveClassification and mis-derived to the Equipment fallback.
      if (!savedLevelI && (node.type === 'symbol' || node.type === 'tag' || node.type === 'connection')) {
        const legacyCategory = node.type === 'symbol' ? (node as SymbolNode).category : undefined
        const r = resolveClassification(node.type, legacyCategory)
        effLevelI = r.levelI
        effLevelII = r.levelII
        effLevelIII = r.levelIII
      }
      if (category === UNCLASSIFIED) {
        // "Needs labeling" is about the SAVED classification, not the derived one: a
        // detected node is still unlabeled even though its type implies a classification.
        if (savedLevelI !== '') continue
      } else if (category && effLevelI !== category) continue
      if (subcategory && effLevelII !== subcategory) continue
      if (type && effLevelIII !== type) continue
      // Tag: a tag node's identifier is `content`; other nodes use `label`.
      const tagText = (node.type === 'tag' ? (node as TagNode).content : (node as SymbolNode).label) ?? ''
      if (tagQ && !tagText.toLowerCase().includes(tagQ)) continue
      if (annQ && !(node.annotation_text ?? '').toLowerCase().includes(annQ)) continue
      if (noteQ && !(node.note ?? '').toLowerCase().includes(noteQ)) continue
      const display = tagText || (node.type === 'symbol' ? (node as SymbolNode).name : '') || node.id
      out.push({ id: node.id, label: display })
    }
    return out
  }, [currentState.nodes, category, subcategory, type, tag, annotation, note])

  const matchIds = useMemo(() => new Set(matches.map((m) => m.id)), [matches])

  // Spotlight the matches on the drawing while the panel is open.
  useEffect(() => {
    onHighlightChange?.(isOpen ? matchIds : EMPTY_SET)
  }, [isOpen, matchIds, onHighlightChange])
  // Clear the spotlight when the panel unmounts.
  useEffect(() => () => onHighlightChange?.(EMPTY_SET), [onHighlightChange])

  // Report focus mode while open; always release the dim when the panel closes/unmounts.
  useEffect(() => {
    onFocusModeChange?.(isOpen && focusMode)
  }, [isOpen, focusMode, onFocusModeChange])
  useEffect(() => () => onFocusModeChange?.(false), [onFocusModeChange])
  // The panel is always mounted (only `isOpen` toggles), so reset focus mode to its
  // default (ON) when it closes — a reopen deterministically starts focused rather than
  // stuck on whatever the annotator last toggled it to.
  useEffect(() => {
    if (!isOpen) setFocusMode(true)
  }, [isOpen])

  const clearAll = useCallback(() => {
    setCategory('')
    setSubcategory('')
    setType('')
    setTag('')
    setAnnotation('')
    setNote('')
  }, [])

  if (!isOpen) return null

  const controlClass =
    'w-full h-9 px-3 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground'
  const labelClass = 'text-sm font-semibold text-foreground mb-1.5 block'

  return (
    <div className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0 border-b border-[#e5e5e5] dark:border-dark-border">
        <h3 className="text-base font-semibold text-foreground">
          {t('graph_editor:labelFilter.title', { defaultValue: 'Filter Objects' })}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={String(t('graph_editor:nodePanel.close', { defaultValue: 'Close' }))}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Filter fields */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="filter-category" className={labelClass}>{t('graph_editor:nodePanel.category', { defaultValue: 'Category' })}</label>
            <select
              id="filter-category"
              value={category}
              onChange={(e) => { setCategory(e.target.value); setSubcategory(''); setType('') }}
              className={controlClass}
            >
              <option value="">{t('graph_editor:labelFilter.any', { defaultValue: 'Any' })}</option>
              {taxonomy.levelIValues.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value={UNCLASSIFIED}>{t('graph_editor:labelFilter.unclassified', { defaultValue: 'Unclassified (needs labeling)' })}</option>
            </select>
          </div>

          <div>
            <label htmlFor="filter-subcategory" className={labelClass}>{t('graph_editor:nodePanel.subcategory', { defaultValue: 'Subcategory' })}</label>
            <select
              id="filter-subcategory"
              value={subcategory}
              onChange={(e) => { setSubcategory(e.target.value); setType('') }}
              disabled={!category || category === UNCLASSIFIED}
              className={controlClass}
            >
              <option value="">{t('graph_editor:labelFilter.any', { defaultValue: 'Any' })}</option>
              {subcategoryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="filter-type" className={labelClass}>{t('graph_editor:nodePanel.type', { defaultValue: 'Type' })}</label>
            <select
              id="filter-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={!subcategory || typeOptions.length === 0}
              className={controlClass}
            >
              <option value="">{t('graph_editor:labelFilter.any', { defaultValue: 'Any' })}</option>
              {typeOptions.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="filter-tag" className={labelClass}>{t('graph_editor:nodePanel.label', { defaultValue: 'Tag' })}</label>
            <input id="filter-tag" type="text" value={tag} onChange={(e) => setTag(e.target.value)} className={controlClass}
              placeholder={String(t('graph_editor:labelFilter.contains', { defaultValue: 'Contains…' }))} />
          </div>

          <div>
            <label htmlFor="filter-annotation" className={labelClass}>{t('graph_editor:nodePanel.annotationText', { defaultValue: 'Annotation Text' })}</label>
            <input id="filter-annotation" type="text" value={annotation} onChange={(e) => setAnnotation(e.target.value)} className={controlClass}
              placeholder={String(t('graph_editor:labelFilter.contains', { defaultValue: 'Contains…' }))} />
          </div>

          <div>
            <label htmlFor="filter-note" className={labelClass}>{t('graph_editor:nodePanel.note', { defaultValue: 'Note' })}</label>
            <input id="filter-note" type="text" value={note} onChange={(e) => setNote(e.target.value)} className={controlClass}
              placeholder={String(t('graph_editor:labelFilter.contains', { defaultValue: 'Contains…' }))} />
          </div>

          {/* FS-623 focus mode: dim everything except the matches so the pink
              spotlight isn't lost among the other colored renderings. */}
          <label htmlFor="filter-focus" className="flex items-center gap-2 pt-1 cursor-pointer select-none">
            <input
              id="filter-focus"
              type="checkbox"
              checked={focusMode}
              onChange={(e) => setFocusMode(e.target.checked)}
              className="h-4 w-4 rounded border-[#e5e5e5] dark:border-dark-border"
            />
            <span className="text-sm text-foreground">
              {t('graph_editor:labelFilter.focusMode', { defaultValue: 'Focus matches (dim everything else)' })}
            </span>
          </label>
        </div>
      </div>

      {/* Footer: match count + clear */}
      <div className="flex items-center justify-between px-6 py-3 border-t border-[#e5e5e5] dark:border-dark-border shrink-0">
        <span className="text-sm text-muted-foreground">
          {hasCriteria
            ? t('graph_editor:labelFilter.matchCount', { count: matches.length, defaultValue: '{{count}} match(es)' })
            : t('graph_editor:labelFilter.noCriteria', { defaultValue: 'Set a filter to highlight objects' })}
        </span>
        <Button variant="outline" size="sm" onClick={clearAll} disabled={!hasCriteria}>
          {t('graph_editor:pendingReview.clearAll', { defaultValue: 'Clear All' })}
        </Button>
      </div>
    </div>
  )
}
